// ImageUploadService — Firebase Storage upload for customer-submitted photos
// (image-type form questions). Identical contract on every Paiper client
// (spec §3 of the Image Questions cross-platform contract):
//
//   Path: form_images/{channelUid}/{formId}/submissions/{firebaseAuthUid}/{uuid}.jpg
//   JPEG, quality 0.8, downscaled so the longest side is <= 2048px.
//   contentType must be set to 'image/jpeg' — the Storage rules reject
//   metadata-less uploads (isReasonableImage() requires image/*, <10MB).
//   The uploader must be signed in (anonymous auth counts).

import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import AuthService from './AuthService';

const MAX_SIDE = 2048;
const JPEG_QUALITY = 0.8;

/** Downscale + re-encode an image file to a JPEG blob, longest side <= 2048px. */
function downscaleToJpeg(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        if (width <= 0 || height <= 0) {
          throw new Error('Image has no dimensions.');
        }
        const longestSide = Math.max(width, height);
        if (longestSide > MAX_SIDE) {
          const scale = MAX_SIDE / longestSide;
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas 2D context unavailable.');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(objectUrl);
            if (!blob) {
              reject(new Error('Failed to encode image.'));
              return;
            }
            resolve(blob);
          },
          'image/jpeg',
          JPEG_QUALITY,
        );
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        reject(error);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not load the selected image.'));
    };
    img.src = objectUrl;
  });
}

export interface UploadFormImageParams {
  file: File;
  channelUid: string;
  formId: string;
}

/** Uploads a customer photo for an image-type form question and returns its
    Storage download URL. Ensures anonymous sign-in has happened first. */
export async function uploadFormImage({
  file,
  channelUid,
  formId,
}: UploadFormImageParams): Promise<string> {
  // Make sure we're signed in (anonymous auth counts) before touching Storage.
  await AuthService.getInstance().getIdToken();
  const uid = AuthService.getInstance().getUserId();
  if (!uid) {
    throw new Error('Could not sign in to upload a photo — please try again.');
  }

  const blob = await downscaleToJpeg(file);
  const path = `form_images/${channelUid}/${formId}/submissions/${uid}/${uuidv4()}.jpg`;
  const storage = getStorage();
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
  return getDownloadURL(storageRef);
}

export default uploadFormImage;
